import React, { useState, useEffect } from 'react';
import { sambutanService } from '../../../services/sambutanService';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
    FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaCheckCircle,
    FaExclamationTriangle, FaEye, FaEyeSlash, FaUser,
    FaImage, FaQuoteLeft
} from 'react-icons/fa';

const ManajemenSambutan = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);

    const [formData, setFormData] = useState({
        nama: '',
        jabatan: '',
        instansi: 'Kota Magelang',
        salam_pembuka: "Assalamu'alaikum Warahmatullahi Wabarakatuh,",
        konten: '',
        quote_highlight: '',
        nip: '',
        is_active: true,
    });

    const modules = {
        toolbar: [
            [{ 'header': [2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['blockquote', 'link'],
            ['clean']
        ]
    };

    const loadData = async () => {
        setLoading(true);
        const result = await sambutanService.getList();
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            nama: '',
            jabatan: '',
            instansi: 'Kota Magelang',
            salam_pembuka: "Assalamu'alaikum Warahmatullahi Wabarakatuh,",
            konten: '',
            quote_highlight: '',
            nip: '',
            is_active: true,
        });
        setFotoFile(null);
        setFotoPreview(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            nama: item.nama || '',
            jabatan: item.jabatan || '',
            instansi: item.instansi || 'Kota Magelang',
            salam_pembuka: item.salam_pembuka || '',
            konten: item.konten || '',
            quote_highlight: item.quote_highlight || '',
            nip: item.nip || '',
            is_active: item.is_active === true || item.is_active === 1,
        });
        setFotoFile(null);
        setFotoPreview(item.foto ? getImageUrl(item.foto) : null);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setFotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'is_active') {
                submitData.append(key, formData[key] ? 1 : 0);
            } else {
                submitData.append(key, formData[key] || '');
            }
        });

        if (fotoFile) {
            submitData.append('foto', fotoFile);
        }

        let result;
        if (editing) {
            result = await sambutanService.update(editing.id, submitData);
        } else {
            result = await sambutanService.create(submitData);
        }

        if (result.success) {
            showNotif(result.message || 'Sambutan berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await sambutanService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Sambutan berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleActive = async (item) => {
        const result = await sambutanService.toggleActive(item.id);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Sambutan</h2>
                    <p className="text-gray-600">Kelola sambutan Kepala BPKAD yang tampil di homepage</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                    <FaPlus className="mr-2" /> Tambah Sambutan
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2E7D32] mx-auto"></div>
                </div>
            ) : dataList.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                    <FaQuoteLeft className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-500 mb-2">Belum Ada Sambutan</h3>
                    <p className="text-gray-400 mb-6">Tambahkan sambutan untuk ditampilkan di homepage</p>
                    <button onClick={handleCreate} className="btn-primary">
                        <FaPlus className="mr-2 inline" /> Tambah Sambutan
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {dataList.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Foto */}
                                <div className="flex-shrink-0">
                                    {item.foto ? (
                                        <img
                                            src={getImageUrl(item.foto)}
                                            alt={item.nama}
                                            className="w-24 h-24 object-cover border-4 border-[#2E7D32]/10"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                            <FaUser className="text-3xl text-white/80" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <div>
                                            <h3 className="text-lg font-bold text-[#0F4C3A]">
                                                {item.nama}
                                            </h3>
                                            <p className="text-sm text-[#2E7D32] font-medium">
                                                {item.jabatan}
                                            </p>
                                            <p className="text-xs text-gray-500">{item.instansi}</p>
                                        </div>
                                        <button
                                            onClick={() => handleToggleActive(item)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${item.is_active
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-500'
                                                }`}
                                        >
                                            {item.is_active ? <FaEye size={10} /> : <FaEyeSlash size={10} />}
                                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                                        </button>
                                    </div>

                                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                        <div
                                            className="text-sm text-gray-600 line-clamp-2 mb-3"
                                            dangerouslySetInnerHTML={{
                                                __html: item.konten
                                                    ? item.konten.replace(/<[^>]*>/g, '').substring(0, 200) + '...'
                                                    : ''
                                            }}
                                        />
                                    </p>

                                    {item.quote_highlight && (
                                        <p className="text-xs italic text-gray-500 mb-3">
                                            "{item.quote_highlight}"
                                        </p>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-2 pt-3 border-t">
                                        <button
                                            onClick={() => handleEdit(item)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-500 hover:bg-blue-50 rounded-lg"
                                        >
                                            <FaEdit size={12} /> Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(item)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg"
                                        >
                                            <FaTrash size={12} /> Hapus
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editing ? 'Edit Sambutan' : 'Tambah Sambutan'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Foto */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FaImage className="inline mr-1" /> Foto Kepala Dinas
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFotoChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: JPG, PNG, WebP. Maks: 5MB. Rekomendasi: 400x400px
                                    </p>
                                    {fotoPreview && (
                                        <div className="mt-3">
                                            <img
                                                src={fotoPreview}
                                                alt="Preview"
                                                className="w-32 h-32 object-cover rounded-full border-4 border-[#2E7D32]/20 mx-auto"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nama <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="nama"
                                            value={formData.nama}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Nama lengkap"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Jabatan <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="jabatan"
                                            value={formData.jabatan}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Kepala BPKAD"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Instansi
                                        </label>
                                        <input
                                            type="text"
                                            name="instansi"
                                            value={formData.instansi}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Kota Magelang"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            NIP
                                        </label>
                                        <input
                                            type="text"
                                            name="nip"
                                            value={formData.nip}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="19650101 199003 1 001"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Salam Pembuka
                                    </label>
                                    <input
                                        type="text"
                                        name="salam_pembuka"
                                        value={formData.salam_pembuka}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Assalamu'alaikum Warahmatullahi Wabarakatuh,"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Konten Sambutan <span className="text-red-500">*</span>
                                    </label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.konten}
                                        onChange={(val) => setFormData(prev => ({ ...prev, konten: val }))}
                                        modules={modules}
                                        placeholder="Tulis isi sambutan di sini..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Quote Highlight (Opsional)
                                    </label>
                                    <textarea
                                        name="quote_highlight"
                                        value={formData.quote_highlight}
                                        onChange={handleInputChange}
                                        rows="2"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Kutipan penting yang akan di-highlight..."
                                    />
                                </div>

                                <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium">Aktifkan Sambutan (Tampil di Homepage)</span>
                                </label>

                                {/* Footer */}
                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Sambutan?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus sambutan dari <strong>{deleteConfirm.nama}</strong>
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenSambutan;